import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Spin, Alert, Typography } from 'antd';
import { api } from '../api/client';
import { useAuthStore } from '../stores/authStore';

/**
 * Keycloak OAuth callback page
 * Handles the authorization code flow callback from Keycloak
 */
const KeycloakCallbackPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setToken, setUser } = useAuthStore();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const errorParam = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        // Check for errors from Keycloak
        if (errorParam) {
          setError(errorDescription || errorParam);
          setTimeout(() => navigate('/login'), 3000);
          return;
        }

        // Validate required parameters
        if (!code || !state) {
          setError('Missing authorization code or state parameter');
          setTimeout(() => navigate('/login'), 3000);
          return;
        }

        // State verification is done on backend
        // Backend generated and stored the state when redirecting to the IdP

        // Exchange code for tokens via backend. The provider MUST match the one
        // used at login: the backend stores the provider in the oauth_state and
        // rejects a callback whose path provider differs ("Provider mismatch").
        // LoginPage records the chosen provider in sessionStorage; fall back to
        // the keycloak alias for the legacy single-provider flow / direct hits.
        const provider = sessionStorage.getItem('sso_provider') || 'keycloak';
        sessionStorage.removeItem('sso_provider');
        const redirectUri = `${window.location.origin}/auth/callback`;
        const response = await api.get(`/auth/sso/${encodeURIComponent(provider)}/callback`, {
          params: {
            code,
            state,
            redirect_uri: redirectUri,
          },
        });

        if (response.data && response.data.data) {
          // Refresh token is delivered via the HttpOnly cookie (Set-Cookie on
          // the callback response); only the access token is in the body.
          const { token, user } = response.data.data;

          setToken(token);
          setUser(user);

          // Redirect to home page
          navigate('/', { replace: true });
        } else {
          throw new Error('Invalid response from server');
        }
      } catch (err: any) {
        setError(err.response?.data?.error || err.message || 'Authentication failed');
        setTimeout(() => navigate('/login'), 3000);
      }
    };

    handleCallback();
  }, [searchParams, navigate, setToken, setUser]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
      }}
    >
      {error ? (
        <div style={{ textAlign: 'center', maxWidth: 500, padding: '0 16px' }}>
          <Alert
            message={t('auth.callbackErrorTitle')}
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Typography.Text type="secondary">
            {t('auth.callbackRedirecting')}
          </Typography.Text>
        </div>
      ) : (
        <div style={{ textAlign: 'center' }}>
          <Spin size="large" style={{ marginBottom: 16 }} />
          <Typography.Title level={4} style={{ marginBottom: 8 }}>
            {t('auth.callbackAuthenticating')}
          </Typography.Title>
          <Typography.Text type="secondary">
            {t('auth.callbackWait')}
          </Typography.Text>
        </div>
      )}
    </div>
  );
};

export default KeycloakCallbackPage;
